import { useState } from "react";
import { MainLayout } from "../components/layout/MainLayout";
import { Header } from "../components/layout/Header";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../components/ui/tabs";
import {
  Plus,
  Trash2,
  Save,
  Search,
  Edit,
  Eye,
  ChefHat,
  X,
  Copy,
  Download,
  Filter,
} from "lucide-react";
import {
  recipesData,
  recipeCategories,
  cuisines,
  units,
  generateRecipeCode,
  generateRecipeName,
} from "../data/recipes";
import { masterItemList } from "../data/masterItems";
import { toast } from "sonner";

export default function Recipes() {
  const [recipes, setRecipes] = useState(recipesData);
  const [searchQuery, setSearchQuery] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [cuisineFilter, setCuisineFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");

  // Dialog states
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [selectedRecipe, setSelectedRecipe] = useState(null);

  // Form state for add/edit
  const [formData, setFormData] = useState({
    itemName: "",
    variationName: "",
    category: "",
    cuisine: "",
    sellingPrice: 0,
    yield: 0,
    yieldUnit: "gm",
    instructions: "",
    prepTime: 0,
    cookTime: 0,
    ingredients: [],
    status: "draft",
  });

  // Ingredient search
  const [ingredientSearch, setIngredientSearch] = useState("");
  const [showIngredientSuggestions, setShowIngredientSuggestions] =
    useState(false);

  // Filter recipes
  const filteredRecipes = recipes.filter((recipe) => {
    const matchesSearch =
      recipe.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      recipe.code.toLowerCase().includes(searchQuery.toLowerCase()) ||
      recipe.itemName.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory =
      categoryFilter === "all" || recipe.category === categoryFilter;
    const matchesCuisine =
      cuisineFilter === "all" || recipe.cuisine === cuisineFilter;
    const matchesStatus =
      statusFilter === "all" || recipe.status === statusFilter;
    return matchesSearch && matchesCategory && matchesCuisine && matchesStatus;
  });

  // Search ingredients from master list
  const ingredientSuggestions =
    ingredientSearch.length >= 2
      ? masterItemList
          .filter((item) =>
            item.name.toLowerCase().includes(ingredientSearch.toLowerCase())
          )
          .slice(0, 10)
      : [];

  // Calculate totals
  const calculateTotals = (ingredients) => {
    const landingCost = ingredients.reduce((sum, ing) => sum + ing.amount, 0);
    return { landingCost };
  };

  // Handle view recipe
  const handleViewRecipe = (recipe) => {
    setSelectedRecipe(recipe);
    setIsViewDialogOpen(true);
  };

  // Handle edit recipe
  const handleEditRecipe = (recipe) => {
    setSelectedRecipe(recipe);
    setFormData({ ...recipe });
    setIsEditDialogOpen(true);
  };

  // Handle add new recipe
  const handleAddRecipe = () => {
    setFormData({
      itemName: "",
      variationName: "",
      category: "",
      cuisine: "",
      sellingPrice: 0,
      yield: 0,
      yieldUnit: "gm",
      instructions: "",
      prepTime: 0,
      cookTime: 0,
      ingredients: [],
      status: "draft",
    });
    setIsAddDialogOpen(true);
  };

  // Handle add ingredient
  const handleAddIngredient = (item) => {
    const newIngredient = {
      id: Date.now().toString(),
      ingredientName: item.name,
      rate: item.lastRate,
      quantity: 0,
      unit: item.unit.toUpperCase(),
      fraction: 1000,
      amount: 0,
    };
    setFormData((prev) => ({
      ...prev,
      ingredients: [...(prev.ingredients || []), newIngredient],
    }));
    setIngredientSearch("");
    setShowIngredientSuggestions(false);
  };

  // Handle update ingredient
  const handleUpdateIngredient = (id, field, value) => {
    setFormData((prev) => ({
      ...prev,
      ingredients: prev.ingredients?.map((ing) => {
        if (ing.id === id) {
          const updated = { ...ing, [field]: value };
          // Recalculate amount when quantity, rate, or fraction changes
          if (
            field === "quantity" ||
            field === "rate" ||
            field === "fraction"
          ) {
            updated.amount =
              (updated.rate * updated.quantity) / updated.fraction;
          }
          return updated;
        }
        return ing;
      }),
    }));
  };

  // Handle remove ingredient
  const handleRemoveIngredient = (id) => {
    setFormData((prev) => ({
      ...prev,
      ingredients: prev.ingredients?.filter((ing) => ing.id !== id),
    }));
  };

  // Handle save recipe (add or edit)
  const handleSaveRecipe = (isEdit) => {
    if (!formData.itemName || !formData.category || !formData.cuisine) {
      toast.error("Please fill in all required fields");
      return;
    }

    const { landingCost } = calculateTotals(formData.ingredients || []);
    const costPercentage = formData.sellingPrice
      ? (landingCost / formData.sellingPrice) * 100
      : 0;

    const recipeData = {
      id: isEdit && selectedRecipe ? selectedRecipe.id : Date.now().toString(),
      code:
        isEdit && selectedRecipe
          ? selectedRecipe.code
          : generateRecipeCode(recipes),
      name: generateRecipeName(formData.itemName || "", formData.variationName),
      itemName: formData.itemName || "",
      variationName: formData.variationName,
      category: formData.category || "",
      cuisine: formData.cuisine || "",
      yield: formData.yield || 0,
      yieldUnit: formData.yieldUnit || "gm",
      ingredients: formData.ingredients || [],
      landingCost,
      sellingPrice: formData.sellingPrice || 0,
      costPercentage,
      instructions: formData.instructions,
      prepTime: formData.prepTime,
      cookTime: formData.cookTime,
      createdAt:
        isEdit && selectedRecipe
          ? selectedRecipe.createdAt
          : new Date().toISOString().split("T")[0],
      updatedAt: new Date().toISOString().split("T")[0],
      status: formData.status,
    };

    if (isEdit) {
      setRecipes((prev) =>
        prev.map((r) => (r.id === recipeData.id ? recipeData : r))
      );
      toast.success("Recipe updated successfully");
      setIsEditDialogOpen(false);
    } else {
      setRecipes((prev) => [...prev, recipeData]);
      toast.success("Recipe added successfully");
      setIsAddDialogOpen(false);
    }
  };

  // Handle duplicate recipe
  const handleDuplicateRecipe = (recipe) => {
    const newRecipe = {
      ...recipe,
      id: Date.now().toString(),
      code: generateRecipeCode(recipes),
      name: `${recipe.name} (Copy)`,
      status: "draft",
      createdAt: new Date().toISOString().split("T")[0],
      updatedAt: new Date().toISOString().split("T")[0],
    };
    setRecipes((prev) => [...prev, newRecipe]);
    toast.success("Recipe duplicated");
  };

  // Handle delete recipe
  const handleDeleteRecipe = (id) => {
    setRecipes((prev) => prev.filter((r) => r.id !== id));
    toast.success("Recipe deleted");
  };

  // Get status badge
  const getStatusBadge = (status) => {
    switch (status) {
      case "active":
        return (
          <Badge className="bg-emerald-500/20 text-emerald-600 border-emerald-500/30">
            Active
          </Badge>
        );
      case "inactive":
        return (
          <Badge className="bg-red-500/20 text-red-600 border-red-500/30">
            Inactive
          </Badge>
        );
      case "draft":
        return (
          <Badge className="bg-amber-500/20 text-amber-600 border-amber-500/30">
            Draft
          </Badge>
        );
      default:
        return null;
    }
  };

  // Recipe form component
  const RecipeForm = ({ isEdit = false }) => (
    <div className="space-y-6 max-h-[70vh] overflow-y-auto px-1">
      {/* Basic Info */}
      <div className="space-y-4">
        <h3 className="font-semibold text-lg">Basic Information</h3>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Recipe Code</Label>
            <Input
              value={
                isEdit && selectedRecipe
                  ? selectedRecipe.code
                  : generateRecipeCode(recipes)
              }
              disabled
              className="bg-muted"
            />
          </div>
          <div className="space-y-2">
            <Label>Status</Label>
            <Select
              value={formData.status}
              onValueChange={(v) =>
                setFormData((prev) => ({ ...prev, status: v }))
              }
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="inactive">Inactive</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Item Name *</Label>
            <Input
              placeholder="e.g., Fried Rice"
              value={formData.itemName}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, itemName: e.target.value }))
              }
            />
          </div>
          <div className="space-y-2">
            <Label>Variation Name (Optional)</Label>
            <Input
              placeholder="e.g., Chilli Garlic Veg"
              value={formData.variationName || ""}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  variationName: e.target.value,
                }))
              }
            />
            <p className="text-xs text-muted-foreground">
              Add variation for different versions of the same dish
            </p>
          </div>
        </div>

        <div className="space-y-2">
          <Label>Auto-Generated Recipe Name</Label>
          <Input
            value={generateRecipeName(
              formData.itemName || "",
              formData.variationName
            )}
            disabled
            className="bg-muted font-medium"
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Category *</Label>
            <Select
              value={formData.category}
              onValueChange={(v) =>
                setFormData((prev) => ({ ...prev, category: v }))
              }
            >
              <SelectTrigger>
                <SelectValue placeholder="Select category" />
              </SelectTrigger>
              <SelectContent>
                {recipeCategories.map((cat) => (
                  <SelectItem key={cat} value={cat}>
                    {cat}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Cuisine *</Label>
            <Select
              value={formData.cuisine}
              onValueChange={(v) =>
                setFormData((prev) => ({ ...prev, cuisine: v }))
              }
            >
              <SelectTrigger>
                <SelectValue placeholder="Select cuisine" />
              </SelectTrigger>
              <SelectContent>
                {cuisines.map((cuisine) => (
                  <SelectItem key={cuisine} value={cuisine}>
                    {cuisine}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-4">
          <div className="space-y-2">
            <Label>Yield Quantity</Label>
            <Input
              type="number"
              value={formData.yield || ""}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  yield: parseFloat(e.target.value) || 0,
                }))
              }
            />
          </div>
          <div className="space-y-2">
            <Label>Yield Unit</Label>
            <Select
              value={formData.yieldUnit}
              onValueChange={(v) =>
                setFormData((prev) => ({ ...prev, yieldUnit: v }))
              }
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {units.map((unit) => (
                  <SelectItem key={unit} value={unit.toLowerCase()}>
                    {unit}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Selling Price (₹)</Label>
            <Input
              type="number"
              value={formData.sellingPrice || ""}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  sellingPrice: parseFloat(e.target.value) || 0,
                }))
              }
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Prep Time (mins)</Label>
            <Input
              type="number"
              value={formData.prepTime || ""}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  prepTime: parseInt(e.target.value) || 0,
                }))
              }
            />
          </div>
          <div className="space-y-2">
            <Label>Cook Time (mins)</Label>
            <Input
              type="number"
              value={formData.cookTime || ""}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  cookTime: parseInt(e.target.value) || 0,
                }))
              }
            />
          </div>
        </div>
      </div>

      {/* Ingredients */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-lg">Ingredients</h3>
          <Badge variant="outline">
            {formData.ingredients?.length || 0} items
          </Badge>
        </div>

        {/* Search Ingredients */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search and add ingredients from master list..."
            value={ingredientSearch}
            onChange={(e) => {
              setIngredientSearch(e.target.value);
              setShowIngredientSuggestions(true);
            }}
            onFocus={() => setShowIngredientSuggestions(true)}
            className="pl-9"
          />
          {showIngredientSuggestions && ingredientSuggestions.length > 0 && (
            <div className="absolute z-50 w-full bg-popover border border-border rounded-lg shadow-lg mt-1 max-h-[200px] overflow-y-auto">
              {ingredientSuggestions.map((item) => (
                <button
                  key={item.id}
                  className="w-full px-3 py-2 text-left hover:bg-accent flex items-center justify-between"
                  onClick={() => handleAddIngredient(item)}
                >
                  <span className="font-medium text-sm">{item.name}</span>
                  <span className="text-xs text-muted-foreground">
                    ₹{item.lastRate}/{item.unit}
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Ingredients Table */}
        {(formData.ingredients?.length || 0) > 0 && (
          <div className="border rounded-lg overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Ingredient</TableHead>
                  <TableHead className="w-20">Qty</TableHead>
                  <TableHead className="w-20">Unit</TableHead>
                  <TableHead className="w-24">Rate (₹)</TableHead>
                  <TableHead className="w-24">Fraction</TableHead>
                  <TableHead className="w-24 text-right">Amount (₹)</TableHead>
                  <TableHead className="w-10"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {formData.ingredients?.map((ing) => (
                  <TableRow key={ing.id}>
                    <TableCell className="font-medium">
                      {ing.ingredientName}
                    </TableCell>
                    <TableCell>
                      <Input
                        type="number"
                        value={ing.quantity || ""}
                        onChange={(e) =>
                          handleUpdateIngredient(
                            ing.id,
                            "quantity",
                            parseFloat(e.target.value) || 0
                          )
                        }
                        className="h-8 w-full"
                      />
                    </TableCell>
                    <TableCell>
                      <Select
                        value={ing.unit}
                        onValueChange={(v) =>
                          handleUpdateIngredient(ing.id, "unit", v)
                        }
                      >
                        <SelectTrigger className="h-8">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {units.map((unit) => (
                            <SelectItem key={unit} value={unit}>
                              {unit}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell>
                      <Input
                        type="number"
                        value={ing.rate || ""}
                        onChange={(e) =>
                          handleUpdateIngredient(
                            ing.id,
                            "rate",
                            parseFloat(e.target.value) || 0
                          )
                        }
                        className="h-8 w-full"
                      />
                    </TableCell>
                    <TableCell>
                      <Input
                        type="number"
                        value={ing.fraction || ""}
                        onChange={(e) =>
                          handleUpdateIngredient(
                            ing.id,
                            "fraction",
                            parseFloat(e.target.value) || 1000
                          )
                        }
                        className="h-8 w-full"
                      />
                    </TableCell>
                    <TableCell className="text-right font-medium">
                      ₹{ing.amount.toFixed(2)}
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-destructive"
                        onClick={() => handleRemoveIngredient(ing.id)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}

        {/* Cost Summary */}
        <Card className="bg-muted/50">
          <CardContent className="p-4">
            <div className="grid grid-cols-3 gap-4 text-center">
              <div>
                <p className="text-sm text-muted-foreground">Landing Cost</p>
                <p className="text-xl font-bold text-primary">
                  ₹
                  {calculateTotals(
                    formData.ingredients || []
                  ).landingCost.toFixed(2)}
                </p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Selling Price</p>
                <p className="text-xl font-bold">
                  ₹{formData.sellingPrice?.toFixed(2) || "0.00"}
                </p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Cost %</p>
                <p className="text-xl font-bold text-emerald-600">
                  {formData.sellingPrice
                    ? (
                        (calculateTotals(formData.ingredients || [])
                          .landingCost /
                          formData.sellingPrice) *
                        100
                      ).toFixed(2)
                    : "0.00"}
                  %
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Instructions */}
      <div className="space-y-2">
        <Label>Cooking Instructions (Optional)</Label>
        <Textarea
          placeholder="Enter step-by-step cooking instructions..."
          value={formData.instructions || ""}
          onChange={(e) =>
            setFormData((prev) => ({ ...prev, instructions: e.target.value }))
          }
          rows={4}
        />
      </div>
    </div>
  );

  return (
    <MainLayout>
      <Header
        title="Recipe Management"
        subtitle="Create, view, and manage recipes with ingredients and costing"
      />

      <div className="p-6 space-y-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="bg-card border-border">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-primary/10">
                  <ChefHat className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{recipes.length}</p>
                  <p className="text-sm text-muted-foreground">Total Recipes</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-card border-border">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-emerald-500/10">
                  <ChefHat className="w-5 h-5 text-emerald-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold">
                    {recipes.filter((r) => r.status === "active").length}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    Active Recipes
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-card border-border">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-amber-500/10">
                  <ChefHat className="w-5 h-5 text-amber-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold">
                    {recipes.filter((r) => r.status === "draft").length}
                  </p>
                  <p className="text-sm text-muted-foreground">Draft Recipes</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-card border-border">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-blue-500/10">
                  <ChefHat className="w-5 h-5 text-blue-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold">
                    {new Set(recipes.map((r) => r.category)).size}
                  </p>
                  <p className="text-sm text-muted-foreground">Categories</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters and Actions */}
        <Card className="bg-card border-border">
          <CardContent className="p-4">
            <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center justify-between">
              <div className="flex flex-wrap gap-3 flex-1">
                <div className="relative flex-1 min-w-[200px] max-w-md">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="Search recipes..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-9"
                  />
                </div>
                <Select
                  value={categoryFilter}
                  onValueChange={setCategoryFilter}
                >
                  <SelectTrigger className="w-[150px]">
                    <SelectValue placeholder="Category" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Categories</SelectItem>
                    {recipeCategories.map((cat) => (
                      <SelectItem key={cat} value={cat}>
                        {cat}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={cuisineFilter} onValueChange={setCuisineFilter}>
                  <SelectTrigger className="w-[150px]">
                    <SelectValue placeholder="Cuisine" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Cuisines</SelectItem>
                    {cuisines.map((cuisine) => (
                      <SelectItem key={cuisine} value={cuisine}>
                        {cuisine}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-[130px]">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="draft">Draft</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button onClick={handleAddRecipe} className="gap-2">
                <Plus className="w-4 h-4" />
                Add Recipe
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Recipes Table */}
        <Card className="bg-card border-border">
          <CardHeader>
            <CardTitle>Recipes ({filteredRecipes.length})</CardTitle>
            <CardDescription>
              Manage your recipe collection with variations and costing
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Code</TableHead>
                  <TableHead>Recipe Name</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Cuisine</TableHead>
                  <TableHead className="text-right">Landing Cost</TableHead>
                  <TableHead className="text-right">Selling Price</TableHead>
                  <TableHead className="text-right">Cost %</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredRecipes.map((recipe) => (
                  <TableRow key={recipe.id}>
                    <TableCell className="font-mono text-sm">
                      {recipe.code}
                    </TableCell>
                    <TableCell>
                      <div>
                        <p className="font-medium">{recipe.name}</p>
                        {recipe.variationName && (
                          <p className="text-xs text-muted-foreground">
                            {recipe.itemName} → {recipe.variationName}
                          </p>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">{recipe.category}</Badge>
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {recipe.cuisine}
                    </TableCell>
                    <TableCell className="text-right font-medium">
                      ₹{recipe.landingCost.toFixed(2)}
                    </TableCell>
                    <TableCell className="text-right">
                      ₹{recipe.sellingPrice.toFixed(2)}
                    </TableCell>
                    <TableCell className="text-right">
                      <span
                        className={
                          recipe.costPercentage < 15
                            ? "text-emerald-600"
                            : recipe.costPercentage < 25
                            ? "text-amber-600"
                            : "text-red-600"
                        }
                      >
                        {recipe.costPercentage.toFixed(2)}%
                      </span>
                    </TableCell>
                    <TableCell>{getStatusBadge(recipe.status)}</TableCell>
                    <TableCell>
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => handleViewRecipe(recipe)}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => handleEditRecipe(recipe)}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => handleDuplicateRecipe(recipe)}
                        >
                          <Copy className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-destructive"
                          onClick={() => handleDeleteRecipe(recipe.id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                {filteredRecipes.length === 0 && (
                  <TableRow>
                    <TableCell
                      colSpan={9}
                      className="text-center py-8 text-muted-foreground"
                    >
                      No recipes found matching your criteria
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>

      {/* View Recipe Dialog */}
      <Dialog open={isViewDialogOpen} onOpenChange={setIsViewDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ChefHat className="w-5 h-5 text-primary" />
              {selectedRecipe?.name}
            </DialogTitle>
            <DialogDescription>
              {selectedRecipe?.code} • {selectedRecipe?.category} •{" "}
              {selectedRecipe?.cuisine}
            </DialogDescription>
          </DialogHeader>
          {selectedRecipe && (
            <div className="space-y-6">
              {/* Overview */}
              <div className="grid grid-cols-4 gap-4">
                <Card className="bg-muted/50">
                  <CardContent className="p-3 text-center">
                    <p className="text-xs text-muted-foreground">Yield</p>
                    <p className="font-semibold">
                      {selectedRecipe.yield} {selectedRecipe.yieldUnit}
                    </p>
                  </CardContent>
                </Card>
                <Card className="bg-muted/50">
                  <CardContent className="p-3 text-center">
                    <p className="text-xs text-muted-foreground">
                      Landing Cost
                    </p>
                    <p className="font-semibold text-primary">
                      ₹{selectedRecipe.landingCost.toFixed(2)}
                    </p>
                  </CardContent>
                </Card>
                <Card className="bg-muted/50">
                  <CardContent className="p-3 text-center">
                    <p className="text-xs text-muted-foreground">
                      Selling Price
                    </p>
                    <p className="font-semibold">
                      ₹{selectedRecipe.sellingPrice.toFixed(2)}
                    </p>
                  </CardContent>
                </Card>
                <Card className="bg-muted/50">
                  <CardContent className="p-3 text-center">
                    <p className="text-xs text-muted-foreground">Cost %</p>
                    <p className="font-semibold text-emerald-600">
                      {selectedRecipe.costPercentage.toFixed(2)}%
                    </p>
                  </CardContent>
                </Card>
              </div>

              {/* Ingredients */}
              <div>
                <h4 className="font-semibold mb-3">
                  Ingredients ({selectedRecipe.ingredients.length})
                </h4>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Ingredient</TableHead>
                      <TableHead className="text-right">Quantity</TableHead>
                      <TableHead>Unit</TableHead>
                      <TableHead className="text-right">Rate (₹)</TableHead>
                      <TableHead className="text-right">Fraction</TableHead>
                      <TableHead className="text-right">Amount (₹)</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {selectedRecipe.ingredients.map((ing) => (
                      <TableRow key={ing.id}>
                        <TableCell className="font-medium">
                          {ing.ingredientName}
                        </TableCell>
                        <TableCell className="text-right">
                          {ing.quantity}
                        </TableCell>
                        <TableCell>{ing.unit}</TableCell>
                        <TableCell className="text-right">
                          {ing.rate.toFixed(2)}
                        </TableCell>
                        <TableCell className="text-right">
                          {ing.fraction}
                        </TableCell>
                        <TableCell className="text-right font-medium">
                          {ing.amount.toFixed(2)}
                        </TableCell>
                      </TableRow>
                    ))}
                    <TableRow className="bg-muted/50">
                      <TableCell
                        colSpan={5}
                        className="font-semibold text-right"
                      >
                        Total Landing Cost
                      </TableCell>
                      <TableCell className="text-right font-bold text-primary">
                        ₹{selectedRecipe.landingCost.toFixed(2)}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </div>

              {/* Instructions */}
              {selectedRecipe.instructions && (
                <div>
                  <h4 className="font-semibold mb-2">Cooking Instructions</h4>
                  <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                    {selectedRecipe.instructions}
                  </p>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsViewDialogOpen(false)}
            >
              Close
            </Button>
            <Button
              onClick={() => {
                setIsViewDialogOpen(false);
                if (selectedRecipe) handleEditRecipe(selectedRecipe);
              }}
            >
              <Edit className="w-4 h-4 mr-2" />
              Edit Recipe
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Recipe Dialog */}
      <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle>Add New Recipe</DialogTitle>
            <DialogDescription>
              Create a new recipe with ingredients and costing. Use variations
              for different versions of the same dish.
            </DialogDescription>
          </DialogHeader>
          <RecipeForm isEdit={false} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsAddDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={() => handleSaveRecipe(false)}>
              <Save className="w-4 h-4 mr-2" />
              Save Recipe
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Recipe Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle>Edit Recipe</DialogTitle>
            <DialogDescription>
              Update recipe details, ingredients, and costing information.
            </DialogDescription>
          </DialogHeader>
          <RecipeForm isEdit={true} />
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsEditDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button onClick={() => handleSaveRecipe(true)}>
              <Save className="w-4 h-4 mr-2" />
              Update Recipe
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </MainLayout>
  );
}
